import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchVehicle,
  fetchQuestions,
  patchVehicle,
  fetchValuation,
  fetchFileStackKeys,
  postImages,
  fetchAppraisal,
  deleteImage,
  fetchVehicleLookupMake,
  fetchVehicleLookupModel,
  fetchVehicleLookupFuelTypes,
  fetchVehicleLookupProductionYears,
  fetchVehicleLookupDerivatives,
  fetchManualVehicle,
  postMultiMatchCapCode,
  fetchTellUsMore,
  fetchSavedQuestionAndAnswersSet
} from "../../api/requests";
import { isHandledVehicleErrorCode } from "../../errors";
import { shouldDisableInsuranceAssumptionAnswer } from "../../helpers/questionLookup";
import { formatQuestions } from "../../pages/helpers";
import { SourceSystemNames } from "../../config/sourceSystemNames";

export const getVehicle = createAsyncThunk("/appraisal/getVehicle", (args) =>
  fetchVehicle(args)
);

export const getQuestions = createAsyncThunk(
  "/appraisal/getQuestions",
  (appraisalSessionId) => fetchQuestions(appraisalSessionId)
);

export const updateVehicle = createAsyncThunk(
  "/appraisal/updateVehicle",
  (args) => patchVehicle(args)
);

export const getValuation = createAsyncThunk(
  "/appraisal/getValuation",
  (args) => fetchValuation(args)
);

export const getFileStackKeys = createAsyncThunk(
  "/appraisal/getFileStackKeys",
  (appraisalSessionId) => fetchFileStackKeys(appraisalSessionId)
);

export const getVehicleLookupMake = createAsyncThunk(
  "/appraisal/vehicleLookupMakes",
  (args) => fetchVehicleLookupMake(args)
);

export const getVehicleLookupModels = createAsyncThunk(
  "/appraisal/vehicleLookupModels",
  (args) => fetchVehicleLookupModel(args)
);

export const getVehicleLookupFuelTypes = createAsyncThunk(
  "/appraisal/vehicleLookupFuelTypes",
  (args) => fetchVehicleLookupFuelTypes(args)
);

export const getVehicleLookupProductionYears = createAsyncThunk(
  "/appraisal/vehicleLookupProductionYears",
  (args) => fetchVehicleLookupProductionYears(args)
);

export const getVehicleLookupDerivatives = createAsyncThunk(
  "/appraisal/vehicleLookupDerivatives",
  (args) => fetchVehicleLookupDerivatives(args)
);

export const postManualVehicle = createAsyncThunk(
  "/appraisal/postManualVehicle",
  (args) => fetchManualVehicle(args)
);

export const postTellUsMore = createAsyncThunk(
  "/appraisal/postTellUsMore",
  (args) => fetchTellUsMore(args)
);

export const addImages = createAsyncThunk("/appraisal/addImages", (args) =>
  postImages(args)
);

export const getAppraisal = createAsyncThunk(
  "/appraisal/getAppraisal",
  (appraisalSessionId) => fetchAppraisal(appraisalSessionId)
);

export const getSavedQuestionAndAnswersSet = createAsyncThunk(
  "/appraisal/getSavedQuestionAndAnswersSet",
  (appraisalSessionId) => fetchSavedQuestionAndAnswersSet(appraisalSessionId)
);

export const removePhoto = createAsyncThunk("/appraisal/removePhoto", (args) =>
  deleteImage(args)
);

export const updateMultiMatchVehicle = createAsyncThunk(
  "/appraisal/updateMultiMatchVehicle",
  /**  @param args {appraisalSessionId: string, selectedCapCode: string} */
  (args) => postMultiMatchCapCode(args)
);

export const appraisalSlice = createSlice({
  name: "appraisal",

  initialState: {
    data: {
      images: [],
      isOnline: true,
      onSubmitPromoError: false,
      promoApplied: false,
      promoLoading: false,
      questionsAndAnswers: {},
      userEnteredMileage: null
    },
    conditionalAnswersSubmitted: [],
    loading: false,
    isReuseAppraisal: false,
    isBlockedRegistration: false,
    error: null,
    fileStack: {},
    vehicleLookup: {},
    registrationInput: "",
    featureToggles: {},
    tellUsMoreSubmitted: false,
    disableInsuranceAssumptionAnswer: false
  },

  reducers: {
    setSourceSystemData: (state, action) => {
      const {
        sourceSystem,
        redirectURI,
        registration,
        exitUri,
        analyticSearchParam
      } = action.payload;

      state.data.sourceSystem = sourceSystem;
      state.data.redirectURI = redirectURI;
      state.data.registration = registration;
      state.data.exitUri = exitUri;
      state.data.sourceSystemAnalyticsParam = analyticSearchParam;
    },
    setAppraisalSessionId: (state, action) => {
      state.data.appraisalSessionId = action.payload;
    },
    removeVehicle: (state) => {
      state.data.vehicle = null;
    },
    removeQuestions: (state) => {
      state.data.questions = null;
    },
    setMileage: (state, action) => {
      state.data.vehicle = {
        ...state.data.vehicle,
        mileage: action.payload
      };

      state.data.userEnteredMileage = action.payload;
    },
    setPromoApplied: (state, action) => {
      state.data.promoApplied = action.payload;
    },
    setOnSubmitPromoError: (state, action) => {
      state.data.onSubmitPromoError = action.payload;
    },
    removeDefaultFactualAndConditionAnswers: (state, action) => {
      state.data.questionsAndAnswers = action.payload;
    },
    setQuestionsAndAnswers: (state, action) => {
      state.data.questionsAndAnswers = {
        ...state.data.questionsAndAnswers,
        ...action.payload
      };
      // any change to questions will trigger a re request of
      // getValuation on initial offer land

      // not doing this for sell to prevent rerequest will not all answers if
      // user changes only some conditions and back navigates to initial offer.
      // if they answer all conditions they will land on personal offer which will
      // request new valuation
      if (state.data.sourceSystem !== "sell") {
        state.data.hasValuation = false;
      }
    },
    setQuestionValidation: (state, action) => {
      state.data.questionValidation = action.payload;
    },
    clearQuestionValidation: (state, action) => {
      state.data.questionValidation = null;
    },
    setConditionalAnswersSubmitted: (state, action) => {
      state.conditionalAnswersSubmitted = action.payload;
    },
    updateConditionalAnswersSubmitted: (state, action) => {
      if (state.conditionalAnswersSubmitted.length > 0) {
        state.conditionalAnswersSubmitted =
          state.conditionalAnswersSubmitted.filter(
            (id) => id !== action.payload
          );
      }
    },
    setPhotosRequired: (state, action) => {
      state.data.photosRequired = {
        ...state.data.photosRequired,
        ...action.payload
      };
    },
    setIsFullValuation: (state, action) => {
      state.data.fullValuation = action.payload;
    },
    setAllQuestionsAnswered: (state, action) => {
      state.data.allQuestionsAnswered = action.payload;
    },
    setQuickValuationPartExchange: (state, action) => {
      state.data.quickValuationPartExchange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setIsQuickValuation: (state, action) => {
      state.data.isQuickValuation = action.payload;
    },
    clearType: (state, action) => {
      state.vehicleLookup[action.payload] = null;
    },
    setVehicleLookupReg: (state, action) => {
      state.vehicleLookup.reg = action.payload;
    },
    setIsManual: (state) => {
      state.isManual = true;
    },
    setRegistration: (state, action) => {
      state.data.registration = action.payload;
    },
    setIsOnline: (state, action) => {
      state.data.isOnline = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setIsLoading: (state, action) => {
      state.loading = action.payload;
    },
    setIsReuseAppraisal: (state, action) => {
      state.isReuseAppraisal = action.payload;
    },
    setIsSavedDealReappraisal: (state, action) => {
      state.isSavedDealReappraisal = action.payload;
    },
    setIsBlockedRegistration: (state, action) => {
      state.isBlockedRegistration = action.payload;
    },
    setImages: (state, action) => {
      state.data.images = action.payload;
    },
    setAnswersChanged: (state, action) => {
      state.answersChanged = action.payload;
    },
    setRegistrationInput: (state, action) => {
      state.registrationInput = action.payload;
    },
    clearRegistrationInput: (state) => {
      state.registrationInput = "";
    },
    setFeatureToggles: (state, action) => {
      const { key, value } = action.payload;
      state.featureToggles = { ...state.featureToggles, [key]: value };
    },
    setTellUsMoreSubmitted: (state, action) => {
      state.tellUsMoreSubmitted = action.payload;
    },
    setTellUsMoreError: (state, action) => {
      state.tellUsMoreError = action.payload;
    },
    tellUsMoreClearError: (state) => {
      state.tellUsMoreError = null;
    },
    clearVehicle: (state) => {
      state.data.vehicle = null;
    },
    setPromoLoading: (state, action) => {
      state.data.promoLoading = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getVehicle.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getVehicle.fulfilled, (state, action) => {
      const { result, error } = action.payload;

      state.loading = false;
      if (result) {
        state.data.vehicle = result;
        state.error = null;
      }
      if (error && !isHandledVehicleErrorCode(error.code)) {
        state.error = error.code;
      }
    });

    builder.addCase(getVehicle.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getQuestions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getQuestions.fulfilled, (state, action) => {
      const { result, error } = action.payload;
      const quickValuation =
        state.data.isQuickValuation || state.data.sourceSystem === SourceSystemNames.SellMyCar;
      const isCnp = state.data.sourceSystem === SourceSystemNames.SalesCNP;
      const isBranchAppraisal = state.data.sourceSystem === SourceSystemNames.BranchAppraisal;
      state.loading = false;
      if (result) {
        const formattedQuestions = formatQuestions(
          result,
          quickValuation,
          isCnp
        );
        if (isBranchAppraisal) {
          state.disableInsuranceAssumptionAnswer =
            shouldDisableInsuranceAssumptionAnswer(formattedQuestions);
        }
        state.data.questions = formattedQuestions;
      }
      if (error) {
        state.error = error.code;
      }
    });

    builder.addCase(getQuestions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(updateVehicle.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(updateVehicle.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });

    builder.addCase(updateVehicle.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getValuation.pending, (state) => {
      state.loading = true;
      state.valuationLoading = true;
      state.error = null;
    });

    builder.addCase(getValuation.fulfilled, (state, action) => {
      const { result, error } = action.payload;

      state.valuationLoading = false;
      state.loading = false;
      // if sell we want to throw any error
      if (state.data.sourceSystem === "sell" && error) {
        state.error = error.code;
      }

      // if this error code we want valuation to continue
      // this returns valuation of 0
      if (result || error.code === "ON-VAL-UA") {
        state.data.hasValuation = true;
        state.data.valuation = result;
      }

      // any other error code we want to throw
      if (error && error.code !== "ON-VAL-UA") {
        state.error = error.code;
      }
    });

    builder.addCase(getValuation.rejected, (state, action) => {
      state.loading = false;
      state.valuationLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(getFileStackKeys.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getFileStackKeys.fulfilled, (state, action) => {
      const { result, error } = action.payload;
      const inFourMins = new Date(
        new Date().getTime() + 4 * 60 * 1000
      ).toISOString();

      state.loading = false;
      if (result) {
        state.fileStack = { keys: result, expires: inFourMins };
      }
      if (error) {
        state.error = null;
      }
    });

    builder.addCase(getVehicleLookupMake.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(getVehicleLookupMake.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getVehicleLookupMake.fulfilled, (state, action) => {
      const { result } = action.payload;
      state.loading = false;
      state.vehicleLookup.manufacturers = result;
    });

    builder.addCase(getVehicleLookupModels.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(getVehicleLookupModels.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getVehicleLookupModels.fulfilled, (state, action) => {
      const { result } = action.payload;
      state.loading = false;
      state.vehicleLookup.models = result;
    });

    builder.addCase(getVehicleLookupFuelTypes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(getVehicleLookupFuelTypes.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getVehicleLookupFuelTypes.fulfilled, (state, action) => {
      const { result } = action.payload;

      state.loading = false;
      state.vehicleLookup.fuelTypes = result;
    });

    builder.addCase(
      getVehicleLookupProductionYears.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error;
      }
    );

    builder.addCase(getVehicleLookupProductionYears.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(
      getVehicleLookupProductionYears.fulfilled,
      (state, action) => {
        const { result } = action.payload;

        state.loading = false;
        state.vehicleLookup.productionYears = result;
      }
    );

    builder.addCase(getVehicleLookupDerivatives.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(getVehicleLookupDerivatives.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getVehicleLookupDerivatives.fulfilled, (state, action) => {
      const { result } = action.payload;

      state.loading = false;
      state.vehicleLookup.derivatives = result;
    });

    builder.addCase(getFileStackKeys.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(addImages.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(addImages.fulfilled, (state, action) => {
      const { images } = action.meta.arg;
      const { error } = action.payload;
      state.loading = false;
      if (!error) {
        state.data.images = [...state.data.images, ...images];
      }
      if (error) {
        state.error = error.code;
      }
    });

    builder.addCase(addImages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(removePhoto.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(removePhoto.fulfilled, (state, action) => {
      const { imageIdentifier } = action.meta.arg;
      const { error } = action.payload;

      state.loading = false;
      if (!error) {
        state.data.images = state.data.images.filter(
          (image) => image.imageIdentifier !== imageIdentifier
        );
      }
      if (error) {
        state.error = error.code;
      }
    });

    builder.addCase(removePhoto.rejected, (state, action) => {
      state.loading = false;
      state.error = "Unable to remove image";
    });

    builder.addCase(getAppraisal.pending, (state) => {
      state.loading = true;
      state.appraisalLoading = true;
      state.error = null;
    });

    builder.addCase(getSavedQuestionAndAnswersSet.pending, (state) => {
      state.loading = true;
      state.appraisalLoading = true;
      state.error = null;
    });

    builder.addCase(getAppraisal.fulfilled, (state, action) => {
      const { result, error } = action.payload;
      state.loading = false;
      state.appraisalLoading = false;

      if (result) {
        state.data.sourceSystemReferenceId = result.sourceSystemReferenceId;
        state.data.sourceSystemName = result.sourceSystemName;
        state.data.vehicle = result.vehicle;
        state.data.images = result.images;
      }
      if (error) {
        state.error = error.code;
      }
    });

    builder.addCase(
      getSavedQuestionAndAnswersSet.fulfilled,
      (state, action) => {
        const { result, error } = action.payload;
        state.loading = false;
        state.appraisalLoading = false;

        if (result) {
          state.data.questionsAndAnswers = action.payload.result;
        }
        if (error) {
          state.error = error.code;
        }
      }
    );

    builder.addCase(getAppraisal.rejected, (state, action) => {
      state.loading = false;
      state.appraisalLoading = false;
      state.error = action.message;
    });

    builder.addCase(getSavedQuestionAndAnswersSet.rejected, (state, action) => {
      state.loading = false;
      state.appraisalLoading = false;
      state.error = action.message;
    });

    builder.addCase(postManualVehicle.rejected, (state, action) => {
      state.error = action.error.message;
    });

    builder.addCase(postManualVehicle.pending, (state, action) => {
      state.error = null;
    });

    builder.addCase(postManualVehicle.fulfilled, (state, action) => {
      const { result } = action.payload;
      state.data.vehicle = result;
    });

    builder.addCase(updateMultiMatchVehicle.rejected, (state, action) => {
      state.error = action.error.message;
    });

    builder.addCase(updateMultiMatchVehicle.pending, (state, action) => {
      state.error = null;
    });

    builder.addCase(updateMultiMatchVehicle.fulfilled, (state, action) => {
      const { result } = action.payload;
      state.data.vehicle = result;
    });
  }
});
